/**
 * @file site-closure.ts
 * @description «Сайт закрыт на работы» — общие константы, DTO и чистые
 * функции паузы сайта магазина (phase19.16, SB138; запрет цифр скидок —
 * SB57; проект данных docs/phases/phase19.16-materials/db-design-site-closed.md
 * §2–§5.2 и «Декомпозиция по ролям» п. 2).
 *
 * Зачем в shared: одно и то же правило «закрыт ли сайт сейчас», список
 * открытых адресов и предел Retry-After читают API (статус витрины, защита
 * чекаута, кабинет), витрина (proxy, заглушка 503) и кабинет (плашка,
 * окно закрытия). Расхождение между ними = покупатель видит заглушку, а
 * заказ проходит (или наоборот), поэтому источник один.
 *
 * Решения оркестратора 2026-10-19:
 *   - Retry-After — до `autoOpenAt ?? expectedOpenAt ?? now + 1 ч`,
 *     в пределах 60…86 400 с (поисковик заглянет не позже чем через сутки);
 *   - на заглушке показывается `expectedOpenAt ?? autoOpenAt`;
 *   - время в DTO — ISO-строки.
 *
 * Модуль без зависимостей от Node и браузера: время передаётся параметром,
 * даты принимаются как `Date | string | null`.
 */

import {
  matchesSitePathRules,
  normalizeSitePath,
  type SitePathMatch,
  type SitePathRule,
} from "./site-consent/site-path-rules.js";
import { deepFreeze } from "./site-theme/deep-freeze.js";

// ── Константы ─────────────────────────────────────────────────────────────

/** Предел текста для покупателей — в кодовых точках, как VARCHAR(200) в Postgres. */
export const SITE_CLOSURE_MESSAGE_MAX = 200;

/** Текст заглушки, если сотрудник не написал свой (SB138). */
export const SITE_CLOSURE_DEFAULT_MESSAGE = "Сайт временно не работает. Мы скоро вернёмся";

/**
 * Текст заглушки при паузе платформы. Нейтральный: причину платформы
 * покупателю не показываем (проект §5.1), а «нарушение» на витрине
 * било бы по репутации магазина до разбора.
 */
export const SITE_CLOSURE_PLATFORM_MESSAGE =
  "Сайт временно недоступен по техническим причинам. Мы скоро вернёмся";

/**
 * phase19.28 (PL8, TD-577) — текст заглушки нового магазина, который ещё не
 * открыт владельцем (пауза «запуск»): нейтральный, без срока и без названия
 * платформы — покупатель видит магазин, а не «технические работы».
 */
export const SITE_CLOSURE_LAUNCH_MESSAGE = "Магазин скоро откроется";

/**
 * phase19.28 (PL9) — подсказка кабинета при паузе «запуск»: что сделать до
 * первого открытия. Жёсткие условия (договор, реквизиты, политика ПДн)
 * проверяет сервер: «Открыть сайт» без них — 422 `site_launch_blocked`,
 * чек-лист — `GET /admin/site-access/launch-check`.
 */
export const SITE_CLOSURE_LAUNCH_CABINET_HINT =
  "Магазин ещё не открыт. Откройте сайт после договора с платформой, заполнения реквизитов и публикации политики обработки персональных данных.";

/** Варианты срока в окне закрытия: через 1 ч / через 3 ч / без срока. */
export const SITE_CLOSURE_AUTO_OPEN_OPTIONS = ["1h", "3h", "none"] as const;
export type SiteClosureAutoOpen = (typeof SITE_CLOSURE_AUTO_OPEN_OPTIONS)[number];

/** Срок в секундах по варианту; `null` — без срока. Считает только сервер по своим часам. */
export const SITE_CLOSURE_AUTO_OPEN: Readonly<Record<SiteClosureAutoOpen, number | null>> = deepFreeze({
  "1h": 3600,
  "3h": 10800,
  none: null,
});

/** За сколько секунд до срока кабинет предлагает «Продлить на 1 ч» (SB138). */
export const SITE_CLOSURE_REMIND_BEFORE_SEC = 600;

/** После стольких суток паузы без срока — предупреждение о поиске (SB138). */
export const SITE_CLOSURE_LONG_PAUSE_DAYS = 3;

/** «Откроем примерно в» — не дальше стольких суток вперёд (ревью аналитика). */
export const SITE_CLOSURE_EXPECTED_MAX_DAYS = 7;

/** Срок пропуска сотрудника: 8 часов (SB138). */
export const SITE_STAFF_PASS_TTL_SEC = 28800;

/** httpOnly-cookie с ключом пропуска — читает только сервер (proxy, API чекаута). */
export const SITE_STAFF_COOKIE = "commora_site_staff";

/** Cookie без секрета (unix-время окончания) — только для полосы сотрудника. */
export const SITE_STAFF_UI_COOKIE = "commora_site_staff_ui";

/** Заголовок, с которым витрина передаёт пропуск в `GET /public/site-status`. */
export const SITE_STAFF_HEADER = "X-Site-Staff";

/**
 * Внутренний заголовок proxy → обработчик `/site-closed`. Строчными:
 * так его отдают `Headers` в Node; proxy вырезает его из входящих запросов.
 */
export const SITE_CLOSED_HEADER = "x-commora-site-closed";

/** Пределы Retry-After (решение оркестратора) и запас «нет сроков». */
export const SITE_CLOSURE_RETRY_AFTER_MIN_SEC = 60;
export const SITE_CLOSURE_RETRY_AFTER_MAX_SEC = 86_400;
export const SITE_CLOSURE_RETRY_AFTER_DEFAULT_SEC = 3600;

// ── Открытые адреса (§5.2) ────────────────────────────────────────────────

/**
 * Как сравнивать адрес с правилом (`segment` / `exact` / `stem`). С Р3г
 * сопоставитель общий с правилами согласия на cookie
 * (site-consent/site-path-rules.ts); прежние имена оставлены обёртками.
 */
export type SiteClosureOpenPathMatch = SitePathMatch;

export type SiteClosureOpenPathRule = SitePathRule;

/**
 * Адреса витрины, открытые при паузе (SB138 «что остаётся открытым»).
 * Страницы конструктора «Условия доставки/возврата» сюда не входят — их
 * адреса приходят динамически в `PublicSiteStatusDto.extraOpenPaths`.
 * phase20 (Paykeeper): адрес возврата после оплаты обязан попасть сюда.
 */
export const SITE_CLOSURE_OPEN_PATHS: readonly SiteClosureOpenPathRule[] = deepFreeze<SiteClosureOpenPathRule[]>([
  // Покупатель с заказом
  { path: "/lk", match: "segment" },
  { path: "/order-confirm", match: "segment" },
  { path: "/checkout/success", match: "segment" },
  // Документы
  { path: "/legal", match: "segment" },
  { path: "/offer", match: "segment" },
  { path: "/privacy", match: "segment" },
  // Служебные (маршруты предпросмотра сами требуют ключ)
  { path: "/site-preview", match: "segment" },
  { path: "/site-staff", match: "segment" },
  { path: "/site-closed", match: "segment" },
  { path: "/preview", match: "segment" },
  { path: "/healthz", match: "segment" },
  // Для поиска и браузера
  { path: "/robots.txt", match: "exact" },
  { path: "/sitemap.xml", match: "exact" },
  { path: "/manifest.webmanifest", match: "exact" },
  { path: "/favicon.ico", match: "exact" },
  { path: "/icon", match: "stem" },
  { path: "/apple-icon", match: "stem" },
  // Статика и API (их отсекает matcher proxy; здесь — чтобы функция была полной)
  { path: "/_next", match: "segment" },
  { path: "/api", match: "segment" },
  { path: "/fonts", match: "segment" },
  { path: "/images", match: "segment" },
]);

/**
 * Приводит адрес к виду для сравнения: без query/hash, с ведущим слэшем,
 * без повторов и завершающего слэша. Регистр не меняем: `/LK` — другой адрес.
 */
export function normalizeSiteClosurePath(pathname: string): string {
  return normalizeSitePath(pathname);
}

/** Открыт ли адрес витрины при паузе (без учёта `extraOpenPaths`). */
export function isSiteClosureOpenPath(pathname: string): boolean {
  return matchesSitePathRules(pathname, SITE_CLOSURE_OPEN_PATHS);
}

// ── DTO (§3, §4) ──────────────────────────────────────────────────────────

/**
 * Кто закрыл: магазин, платформа или «запуск» — новый магазин ещё не открыт
 * (phase19.28, PL8). Сила: платформа > запуск > магазин.
 */
export type SiteClosureSource = "shop" | "platform" | "launch";

/** Как открыли: вручную, по сроку, платформой. */
export type SiteClosureOpenedVia = "manual" | "schedule" | "platform";

/** Сотрудник в DTO: name = displayName ?? email. */
export interface SiteClosureActorDto {
  id: string;
  name: string;
}

export interface SiteClosureDto {
  id: string;
  source: SiteClosureSource;
  message: string;
  expectedOpenAt: string | null;
  autoOpenAt: string | null;
  closedAt: string;
  /** null — сотрудник удалён или пауза платформы. */
  closedBy: SiteClosureActorDto | null;
  /** Только у source = platform. */
  platformReason: string | null;
}

/** `GET /admin/site-access`. */
export interface SiteAccessStateDto {
  /** Кабинет считает поправку к своим часам. */
  serverTime: string;
  /** Shop.timeZone — «откроется сам в 15:20». */
  timeZone: string;
  /** Есть ли SHOP_MANAGE_SETTINGS. */
  canManage: boolean;
  shopClosure: SiteClosureDto | null;
  platformClosure: SiteClosureDto | null;
  /**
   * phase19.28 (PL8) — пауза «запуск»: магазин создан и ещё не открыт.
   * Снимается тем же «Открыть сайт», что и пауза магазина. Необязательное
   * поле — старый кабинет без него продолжает работать.
   */
  launchClosure?: SiteClosureDto | null;
}

/** Строка истории пауз. */
export interface SiteClosureHistoryItemDto extends SiteClosureDto {
  /** У просроченной, ещё не закрытой cron, — autoOpenAt. */
  openedAt: string | null;
  openedBy: SiteClosureActorDto | null;
  /** null — пауза действует. */
  openedVia: SiteClosureOpenedVia | null;
}

/** `GET /admin/site-access/history`. */
export interface SiteClosureHistoryPageDto {
  items: SiteClosureHistoryItemDto[];
  nextCursor: string | null;
}

/** Тело `POST /admin/site-access/close` (сам срок считает сервер по варианту). */
export interface SiteClosureCloseRequest {
  message?: string | null;
  expectedOpenAt?: string | null;
  autoOpen: SiteClosureAutoOpen;
}

/** Действующая пауза в статусе витрины. */
export interface PublicSiteClosureDto {
  source: SiteClosureSource;
  message: string;
  expectedOpenAt: string | null;
  autoOpenAt: string | null;
  closedAt: string;
}

/** Кто смотрит закрытый сайт: только при заголовках X-Site-Staff / X-Site-Preview. */
export type PublicSiteStatusViewer =
  | { kind: "staff" | "preview"; expiresAt: string }
  | { kind: "invalid"; reason: "expired" | "other" };

/** `GET /public/site-status`. */
export interface PublicSiteStatusDto {
  closed: boolean;
  /** Действующая пауза (платформа сильнее магазина); null — сайт открыт. */
  closure: PublicSiteClosureDto | null;
  /** Адреса, открытые при паузе сверх списка платформы (страницы «Условия доставки/возврата»). */
  extraOpenPaths: string[];
  viewer: PublicSiteStatusViewer | null;
  serverTime: string;
}

// ── Время (§2) ────────────────────────────────────────────────────────────

/** Дата из БД, DTO или часов: строка ISO, Date или «нет». */
export type SiteClosureDateInput = Date | string | null;

/** Миллисекунды или null: отсутствующая и непарсящаяся дата одинаково «нет». */
function toMs(value: SiteClosureDateInput): number | null {
  if (value === null) return null;
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Пауза действует: не открыта вручную и срок (если есть) не наступил.
 * Срок проверяется при каждом чтении — сайт открывается вовремя без cron.
 * Битый `autoOpenAt` считаем наступившим: по SB138 «нет данных → сайт открыт».
 */
export function isSiteClosureActive(
  c: { openedAt: SiteClosureDateInput; autoOpenAt: SiteClosureDateInput },
  now: Date,
): boolean {
  if (c.openedAt !== null) return false;
  if (c.autoOpenAt === null) return true;
  const auto = toMs(c.autoOpenAt);
  return auto !== null && auto > now.getTime();
}

/** Действующая пауза магазина из уже активных: платформа > запуск > магазин. */
export function effectiveSiteClosure<T extends { source: SiteClosureSource }>(active: readonly T[]): T | null {
  return (
    active.find((c) => c.source === "platform") ??
    active.find((c) => c.source === "launch") ??
    active.find((c) => c.source === "shop") ??
    null
  );
}

/**
 * Retry-After для 503: секунды до `autoOpenAt ?? expectedOpenAt ?? now + 1 ч`,
 * в пределах 60…86 400. Прошедший срок → 60 (заглянуть скоро).
 */
export function siteClosureRetryAfterSec(
  c: { autoOpenAt: SiteClosureDateInput; expectedOpenAt: SiteClosureDateInput },
  now: Date,
): number {
  const target = toMs(c.autoOpenAt) ?? toMs(c.expectedOpenAt);
  const sec =
    target === null ? SITE_CLOSURE_RETRY_AFTER_DEFAULT_SEC : Math.ceil((target - now.getTime()) / 1000);
  return Math.min(SITE_CLOSURE_RETRY_AFTER_MAX_SEC, Math.max(SITE_CLOSURE_RETRY_AFTER_MIN_SEC, sec));
}

/**
 * Время «откроемся» на заглушке: `expectedOpenAt ?? autoOpenAt`
 * (решение оркестратора — покупателю полезнее знать хоть какое-то время).
 */
export function siteClosureShownOpenAt<T extends SiteClosureDateInput>(c: {
  autoOpenAt: T;
  expectedOpenAt: T;
}): T | null {
  return c.expectedOpenAt ?? c.autoOpenAt ?? null;
}

/** Срок автооткрытия по варианту окна: `now + 1 ч | 3 ч` или null. Только часы сервера. */
export function siteClosureAutoOpenAt(autoOpen: SiteClosureAutoOpen, now: Date): Date | null {
  const sec = SITE_CLOSURE_AUTO_OPEN[autoOpen];
  return sec === null ? null : new Date(now.getTime() + sec * 1000);
}

// ── Текст для покупателей ─────────────────────────────────────────────────

/** Длина текста в кодовых точках — так её считает Postgres VARCHAR. */
export function siteClosureMessageLength(text: string): number {
  return [...text].length;
}

/** Текст после trim; пусто → `SITE_CLOSURE_DEFAULT_MESSAGE`. Длину не режет — это решает вызывающий. */
export function normalizeSiteClosureMessage(text: string | null | undefined): string {
  const trimmed = (text ?? "").trim();
  return trimmed === "" ? SITE_CLOSURE_DEFAULT_MESSAGE : trimmed;
}

/**
 * Признаки цифры скидки (SB57: в свободном тексте цифр скидок нет).
 *   - процент: «10%», «−20 %», «15 процентов»;
 *   - «скидк…» рядом с числом в пределах одной фразы: «скидка 10», «10 ₽ скидки»;
 *   - число со знаком минус: «−20», «-15». Минус после буквы или цифры
 *     («9-18», «123-45-67», «2026-10-19») — диапазон, телефон или дата, не
 *     скидка; с пробелом после («9 – 18») — тоже диапазон; время («-17:00») —
 *     не скидка.
 * «Рядом» — в пределах 20 знаков без точки, запятой и других границ
 * фразы: «Работаем 24 часа, скидок нет» скидкой не считается.
 * Число рядом со «скидк…» не считается скидкой, если это дата, время, год
 * или номер (`isNonDiscountNumber`) — правка по ревью API 2026-10-19:
 * «Скидки вернутся 5 ноября» — не цифра скидки. Процент и «−N» — без исключений.
 */
const DISCOUNT_PATTERNS: readonly RegExp[] = [
  /[−–-]?\d+(?:[.,]\d+)?\s*(?:%|процент[а-яё]*)/iu,
  /(?<![\p{L}\p{N}])[−–-]\d+(?![\d:])/u,
];

/** Все числа текста — кандидаты для правила «скидк… рядом с числом». */
const NUMBER = /\d+(?:[.,]\d+)?/gu;
/** «скидк…» после числа: «500 ₽ скидки». Ленивый промежуток — ближайшее слово. */
const WORD_AFTER_NUMBER = /^[^\d\n.,;!?]{0,20}?скидк[а-яё]*/iu;
/** «скидк…» перед числом: «скидки до 30». Якорь на конце — слово перед этим числом. */
const WORD_BEFORE_NUMBER = /скидк[а-яё]*[^\d\n.,;!?]{0,20}$/iu;
/** Хвост «%» у числа после «скидк…»: «скидки до 30 %». */
const PERCENT_TAIL = /^\s*%/u;

/** Месяц полностью («ноября», «март») или сокращением («ноя.», «сент»). */
const MONTH_AFTER =
  /^\s+(?:январ[ьяе]|феврал[ьяе]|март[ае]?|апрел[ьяе]|ма[йяе]|июн[ьяе]|июл[ьяе]|август[ае]?|сентябр[ьяе]|октябр[ьяе]|ноябр[ьяе]|декабр[ьяе]|янв|фев|мар|апр|июн|июл|авг|сент?|окт|ноя|дек)\.?(?![а-яё])/iu;
/** «5 числа». */
const DAY_WORD_AFTER = /^\s+числ[а-яё]*/iu;
/** Время с двоеточием: «17:00» — и часы, и минуты. */
const CLOCK_AFTER = /^:\d/u;
const CLOCK_BEFORE = /:$/u;
/** Часы после «с/до/в»: «до 18», «с 9 до 18». */
const HOUR_PREFIX = /(?:^|[^\p{L}])(?:с|до|в)\s+$/iu;
/** Деньги или проценты за числом — это уже не часы. */
const MONEY_OR_PERCENT_AFTER = /^\s*(?:%|₽|руб|р\.|процент)/iu;
/** Номер: «№ 5», «тел. 123». */
const NUMBER_SIGN_BEFORE = /(?:№|(?<![\p{L}])тел[а-яё]*\.?)\s*$/iu;
/** Год: 19xx / 20xx. */
const YEAR = /^(?:19|20)\d{2}$/u;

/**
 * Число в позиции [start, end) — дата, время, год или номер, а не цифра скидки.
 * Исключения работают только для правила «скидк… рядом с числом».
 */
function isNonDiscountNumber(text: string, start: number, end: number): boolean {
  const value = text.slice(start, end);
  const before = text.slice(Math.max(0, start - 12), start);
  const after = text.slice(end, end + 16);
  if (MONTH_AFTER.test(after) || DAY_WORD_AFTER.test(after)) return true;
  if (CLOCK_AFTER.test(after) || CLOCK_BEFORE.test(before)) return true;
  if (YEAR.test(value)) return true;
  if (NUMBER_SIGN_BEFORE.test(before)) return true;
  if (/^\d+$/u.test(value) && HOUR_PREFIX.test(before) && !MONEY_OR_PERCENT_AFTER.test(after)) {
    const hour = Number(value);
    if (hour >= 1 && hour <= 24) return true;
  }
  return false;
}

/** Первая находка правила «скидк… рядом с числом» (с исключениями) или null. */
function wordNearNumber(text: string): { index: number; term: string } | null {
  let best: { index: number; term: string } | null = null;
  for (const num of text.matchAll(NUMBER)) {
    const start = num.index;
    const end = start + num[0].length;
    if (isNonDiscountNumber(text, start, end)) continue;
    const before = WORD_BEFORE_NUMBER.exec(text.slice(0, start));
    if (before !== null) {
      const tail = PERCENT_TAIL.exec(text.slice(end));
      const term = text.slice(before.index, end + (tail?.[0].length ?? 0));
      if (best === null || before.index < best.index) best = { index: before.index, term };
    }
    const after = WORD_AFTER_NUMBER.exec(text.slice(end));
    if (after !== null && (best === null || start < best.index)) {
      best = { index: start, term: text.slice(start, end + after[0].length) };
    }
  }
  return best;
}

/**
 * Первый фрагмент текста, похожий на цифру скидки, или null.
 * Кабинет предупреждает заранее, API блокирует `400 site_closure_text_discount`.
 * При совпадении в одной позиции берём самый длинный фрагмент («−20 %», а не «−20»).
 */
export function siteClosureDiscountTerm(text: string): string | null {
  const candidates: { index: number; term: string }[] = [];
  for (const pattern of DISCOUNT_PATTERNS) {
    const found = pattern.exec(text);
    if (found !== null) candidates.push({ index: found.index, term: found[0] });
  }
  const near = wordNearNumber(text);
  if (near !== null) candidates.push(near);
  let best: { index: number; term: string } | null = null;
  for (const c of candidates) {
    const term = c.term.trim();
    if (best === null || c.index < best.index || (c.index === best.index && term.length > best.term.length)) {
      best = { index: c.index, term };
    }
  }
  return best?.term ?? null;
}
